import { getStyle, getStyleProperty } from 'utils';
import PASSIVE_SUPPORT from 'constants/passive-events';
import { DRAG_WORKSPACE } from 'constants/drag';
import DragManager from 'managers/drag.manager';
import DropManager from 'managers/drop.manager';
import { Position } from 'types';

const TRANSFORM_PROPERTY = getStyleProperty('transform');

type Options = {
  id: null | string,
  dragDistance: null | number,
  position: null | Position,
  handle: null | HTMLElement[],
  type: 'workspace' | 'library',
};

const OPTIONS: Options = {
  id: null,
  dragDistance: null,
  position: null,
  handle: null,
  type: DRAG_WORKSPACE,
};

// hash of events to be bound after start event
// mouseup in every case because of programatically triggered mouse ups for simulated events
const POST_START_EVENTS = {
  mousedown: ['mousemove', 'mouseup'],
  touchstart: ['touchmove', 'touchend', 'touchcancel', 'mouseup'],
  pointerdown: ['pointermove', 'pointerup', 'pointercancel', 'mouseup'],
};

class Draggable {
  private options: Options;
  private handles: HTMLElement[] = [];
  private throttle = false;
  private helper: any;
  private startEventType = '';
  private pointerIdentifier: number | null = null;

  public element: HTMLElement;
  public position: Position;
  public startPoint: Position;
  public startPosition: Position = { x: 0, y: 0 };
  public dragPoint: Position;
  public isDragging = false;

  public onDragStart?: null | ((arg: any) => void);
  public onLibraryDragStart?: null | (() => void);
  public onDistanceDragged?: null | ((...args: [Position, Position, Position, string]) => void);
  public onDragEnd?: null | ((arg: any) => void);

  constructor(element: HTMLElement, options: Partial<Options> = {}) {
    this.element = element;
    this.position = {
      x: -1,
      y: -1,
    };
    this.startPoint = {
      x: 0,
      y: 0,
    };
    this.dragPoint = {
      x: 0,
      y: 0,
    };

    this.options = { ...OPTIONS, ...options };
    this._create(this.options);
  }

  private _create(options: Options) {
    if (this.isWorkspace) {
      this.position = options.position ? options.position : this._getPosition();
    }

    this.startPosition = {
      ...this.position,
    };
    this.setHandles();
  }

  get isWorkspace() {
    return this.options.type === DRAG_WORKSPACE;
  }
  get id() {
    return this.options.id;
  }

  // set handle of dragging object
  private setHandles() {
    if (this.options.handle) {
      this.handles = this.options.handle;
    } else {
      this.handles = [this.element];
    }

    this.addListenersToHandles();
  }
  private addListenersToHandles() {
    this.handles.forEach((handle) => {
      handle.addEventListener('mousedown', this as any, false);
      handle.addEventListener('touchstart', this as any, false);

      if (window.navigator.pointerEnabled) {
        handle.addEventListener('pointerdown', this as any, false);
        handle.style.touchAction = 'none';
      }
    });
  }
  private removeHandlerListeners() {
    this.handles.forEach((handle) => {
      handle.removeEventListener('mousedown', this as any, false);
      handle.removeEventListener('touchstart', this as any, false);

      handle.removeEventListener('pointerdown', this as any, false);
    });
  }

  public setPosition(position: Position) {
    this.position = position;
  }
  // get left/top position from style
  private _getPosition() {
    const style = getStyle(this.element);
    const isInitialPosition = (pos: string) => pos === '0px' || pos === 'auto';
    if (style.position !== 'absolute' || (isInitialPosition(style.left) && isInitialPosition(style.top))) {
      const rect = this.element.getBoundingClientRect();
      this.position = {
        x: rect.left,
        y: rect.top,
      };

      return this.position;
    }

    this.position = {
      x: parseInt(style.left, 10),
      y: parseInt(style.top, 10),
    };

    return this.position;
  }

  // @ts-ignore
  // trigger handler methods forevents
  private handleEvent(event: TouchEvent | MouseEvent | PointerEvent) {
    const method = `on${ event.type }`;
    if ((this as any)[method]) {
      (this as any)[method](event);
    }
  }

  // @ts-ignore
  private onmousedown(event: MouseEvent) {
    const button = event.button;
    if (button && (button !== 0 && button !== 1)) {
      return;
    }

    this.dragStart(event, event);
  }
  // @ts-ignore
  private ontouchstart(event: TouchEvent) {
    if (this.isDragging) {
      return;
    }

    this.dragStart(event, event.changedTouches[0]);
  }
  // @ts-ignore
  private onMSPointerDown(event: PointerEvent) {
    this.onpointerdown(event);
  }
  private onpointerdown(event: PointerEvent) {
    if (this.isDragging) {
      return;
    }

    this.dragStart(event, event);
  }

  private setPointerPoint(point: Position, pointer: MouseEvent) {
    point.x = pointer.pageX !== undefined ? pointer.pageX : pointer.clientX;
    point.y = pointer.pageY !== undefined ? pointer.pageY : pointer.clientY;
  }

  private dragStart(event: MouseEvent | TouchEvent | PointerEvent, pointer: any) {
    if (DragManager.isDragging) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.throttle = false;
    this.helper = null;

    this.isDragging = true;

    // save pointer identifier to match up touch events
    this.pointerIdentifier = pointer.pointerId !== undefined ?
      pointer.pointerId :
      (pointer as any).identifier;

    // point where drag began
    this.setPointerPoint(this.startPoint, pointer);
    // if not simulated then get position
    // if simulated then position was passed
    if (!(event as any).simulated || !this.position) {
      this._getPosition();
    }

    // position _when_ drag began
    this.startPosition.x = this.position.x;
    this.startPosition.y = this.position.y;

    this.dragPoint.x = 0;
    this.dragPoint.y = 0;

    this.startEventType = (event as any).startEventType ? (event as any).startEventType : event.type;

    // bind move and end events
    // @ts-ignore
    this._bindEvents(POST_START_EVENTS[this.startEventType]);

    if (this.isWorkspace) {
      this.startAnimatedDrag();
    } else if (this.hasOwnProperty('onLibraryDragStart') && typeof this.onLibraryDragStart === 'function') {
      this.onLibraryDragStart();
    }
  }

  private startAnimatedDrag() {
    this.helper = this.element;
    this.setLeftTop();

    if (this.hasOwnProperty('onDragStart') && typeof this.onDragStart === 'function') {
      this.onDragStart(this);
    }

    DragManager.onDragStart(this.id);

    this.animate();
  }

  private boundEvents: string[] | null = [];
  private _bindEvents(events: string[]) {
    events.forEach((event) => {
      window.addEventListener(event, this as any, false && PASSIVE_SUPPORT);
    });

    // save for unbind call
    this.boundEvents = events;
  }
  private _unbindEvents() {
    const events = this.boundEvents;
    if (!events) {
      return;
    }

    events.forEach((event) => {
      window.removeEventListener(event, this as any, false && PASSIVE_SUPPORT);
    });

    this.boundEvents = null;
  }

  private _getTouch(event: TouchEvent) {
    if (!this.pointerIdentifier) {
      const touch = event.changedTouches[0];
      this.pointerIdentifier = touch.identifier;
      return touch;
    }

    for (let i = 0, ii = event.changedTouches.length; i < ii; i++) {
      if (this.pointerIdentifier === event.changedTouches[i].identifier) {
        return event.changedTouches[i];
      }
    }

    return null;
  }

  // @ts-ignore
  private onmousemove(event: MouseEvent) {
    this.dragMove(event, event);
  }
  // @ts-ignore
  private ontouchmove(event: TouchEvent) {
    const touch = this._getTouch(event);
    if (touch) {
      this.dragMove(event, touch);
    }
  }
  // @ts-ignore
  private onpointermove(event: PointerEvent) {
    if (!this.pointerIdentifier) {
      this.pointerIdentifier = event.pointerId;
    }

    event.preventDefault();
    if (this.pointerIdentifier === event.pointerId) {
      this.dragMove(event, event);
    }
  }

  private dragMove(_event: MouseEvent | TouchEvent | PointerEvent, pointer: any) {
    if (this.isWorkspace && this.throttle) {
      return;
    }
    this.throttle = true;

    if (!this.isDragging) {
      return;
    }

    this.setPointerPoint(this.dragPoint, pointer);
    const dragX = this.dragPoint.x - this.startPoint.x;
    const dragY = this.dragPoint.y - this.startPoint.y;

    if (!this.isWorkspace && this.options.dragDistance) {
      const distance = Math.sqrt((dragX * dragX) + (dragY * dragY));
      if (distance > this.options.dragDistance) {
        this.dragEnd();
        if (this.hasOwnProperty('onDistanceDragged') && typeof this.onDistanceDragged === 'function') {
          this.onDistanceDragged(
            this.startPoint, this.dragPoint, this.startPosition, this.startEventType,
          );
        }
      }

      return;
    }

    this.position.x = this.startPosition.x + dragX;
    this.position.y = this.startPosition.y + dragY;

    // set dragPoint properties
    this.dragPoint.x = dragX;
    this.dragPoint.y = dragY;

    DropManager.onDragMove(this.position);
  }

  // @ts-ignore
  private onmouseup(event: MouseEvent) {
    this.dragEnd(event);
  }
  // @ts-ignore
  private ontouchend(event: TouchEvent) {
    const touch = this._getTouch(event);
    if (touch) {
      this.dragEnd(event);
    }
  }
  // @ts-ignore
  private onpointerup(event: PointerEvent) {
    if (this.pointerIdentifier === event.pointerId) {
      this.dragEnd(event);
    }
  }
  // @ts-ignore
  private onpointercancel(event: PointerEvent) {
    if (event.pointerId === this.pointerIdentifier) {
      this.dragEnd(event);
    }
  }
  // @ts-ignore
  private ontouchcancel(event: TouchEvent) {
    const touch = this._getTouch(event);
    if (touch) {
      this.dragEnd(event);
    }
  }

  private dragEnd(event?: MouseEvent | TouchEvent | PointerEvent) {
    this.isDragging = false;
    this.pointerIdentifier = null;

    this._unbindEvents();

    if (!this.isWorkspace) {
      return;
    }

    if (this.helper && this.helper.style.display !== 'none') {
      if (TRANSFORM_PROPERTY) {
        this.helper.style[TRANSFORM_PROPERTY] = '';
        this.setLeftTop();
      }
    }

    if (this.hasOwnProperty('onDragEnd') && typeof this.onDragEnd === 'function') {
      this.onDragEnd(this);
    }

    DragManager.onDragEnd(this.position, (event as any).simulated || false);
  }

  // -------------------------- animation -------------------------- //
  private animate() {
    this.throttle = false;
    // only render and animate ifdragging
    if (!this.isDragging) {
      return;
    }

    this.transform();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    // tslint:disable only-arrow-functions
    // eslint-disable-next-line func-names, prefer-arrow-callback
    window.requestAnimationFrame(function() {
      self.animate();
    });
  }
  private transform() {
    this.helper.style[TRANSFORM_PROPERTY] = `translate(${ this.dragPoint.x }px,${ this.dragPoint.y }px)`;
  }
  private setLeftTop() {
    this.helper.style.left = `${ this.position.x }px`;
    this.helper.style.top = `${ this.position.y }px`;
  }

  public destroy() {
    this.onDragStart = null;
    this.onDragEnd = null;
    this.onDistanceDragged = null;
    this.onLibraryDragStart = null;

    this._unbindEvents();
    this.removeHandlerListeners();
  }
}


export default Draggable;
